import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Search, Filter, ArrowRight, Wrench, Package, ChevronDown } from 'lucide-react';
import { getPublicApiUrl } from '@/lib/publicApi';

const API_URL = getPublicApiUrl();

export default function PublicMachinesPage() {
  const { settings } = useOutletContext() || {};
  const [machines, setMachines] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [machinesRes, typesRes] = await Promise.all([
        fetch(`${API_URL}/api/public/machines`),
        fetch(`${API_URL}/api/public/material-types`)
      ]);
      const machinesData = await machinesRes.json();
      const typesData = await typesRes.json();
      
      setMachines(machinesData);
      setMaterialTypes(typesData);
      
      // Extract unique brands
      const uniqueBrands = [...new Set(machinesData.map(m => m.brand).filter(Boolean))].sort();
      setBrands(uniqueBrands);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMachines = machines.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || m.material_type_id === filterType;
    const matchesBrand = filterBrand === 'all' || m.brand === filterBrand;
    return matchesSearch && matchesType && matchesBrand;
  });

  // Group by brand
  const groupedMachines = filteredMachines.reduce((acc, machine) => {
    const brand = machine.brand || 'Autres';
    if (!acc[brand]) acc[brand] = [];
    acc[brand].push(machine);
    return acc;
  }, {});

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/')) return `${API_URL}${url}`;
    return url;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Nos <span className="text-yellow-500">Machines</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Découvrez notre gamme complète de mini-pelles, excavatrices et équipements pour tous vos travaux.
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white shadow-sm sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full md:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher une machine..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            {/* Type Filter */}
            <div className="relative w-full md:w-48">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="all">Tous les types</option>
                {materialTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            </div>

            {/* Brand Filter */}
            <div className="relative w-full md:w-48">
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="all">Toutes les marques</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Machines Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-20 text-slate-500">
              <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              Chargement des machines...
            </div>
          ) : filteredMachines.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">Aucune machine trouvée</p>
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-yellow-600 hover:text-yellow-700"
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          ) : (
            Object.entries(groupedMachines).map(([brand, brandMachines]) => (
              <div key={brand} className="mb-12">
                <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed' }}>
                  <span className="w-1 h-8 bg-yellow-500 rounded-full"></span>
                  {brand}
                  <span className="text-sm font-normal text-slate-500">({brandMachines.length} machines)</span>
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {brandMachines.map((machine) => (
                    <Link
                      key={machine.id}
                      to={`/portail/machines/${machine.id}`}
                      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                    >
                      <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
                        {getImageUrl(machine.image_url) ? (
                          <img
                            src={getImageUrl(machine.image_url)}
                            alt={machine.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <Wrench className="w-16 h-16" />
                          </div>
                        )}
                        {machine.warranty && (
                          <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                            {machine.warranty}
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <h3 className="text-lg font-semibold text-slate-800 group-hover:text-yellow-600 transition">
                              {machine.name}
                            </h3>
                            {machine.model && (
                              <p className="text-sm text-slate-500">{machine.model}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Specs */}
                        <div className="flex flex-wrap gap-2 mt-3 mb-4">
                          {machine.weight && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                              {machine.weight}
                            </span>
                          )}
                          {machine.power && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                              {machine.power}
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        {machine.website_description && (
                          <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                            {machine.website_description}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center gap-1 text-yellow-600 font-medium text-sm group-hover:text-yellow-700 transition">
                            Demander un prix
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Vous ne trouvez pas ce que vous cherchez ?
          </h2>
          <p className="text-yellow-100 mb-8">
            Contactez-nous, nous pouvons commander la machine qu'il vous faut.
          </p>
          <Link
            to="/portail/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition"
          >
            Nous contacter
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
